import { useEffect, useRef, useState } from 'react';
import { api, type MediaKey, type GuestProfile, type DoorInfo, type RoomComment, type RoomGuest, type RoomPoll } from '../lib/api';
import { joinMedia, mount, sendToRoom, type Seat } from '../lib/media';
import { encodeStageMessage, decodeStageMessage, isSuperChat, money, nextLiveLine, pollShares, reactionBurst, EMOJI, type StageMessage } from '../lib/live-chat';
import { Floaters } from './Floaters';
import type { Room } from 'livekit-client';

/**
 * The green room (G1): what a guest sees. No account. They open the show's
 * link, give a name, check their camera, and wait. When the host admits them
 * the page says so; when a media server is configured it hands the page a key
 * and the guest's camera and sound join the room.
 */
const KEY = 'rookery.green';

export function GreenRoom({ slug }: { slug: string }) {
  const [room, setRoom] = useState<DoorInfo | null>(null);
  const [name, setName] = useState('');
  const [guest, setGuest] = useState<{ id: string; secret: string } | null>(() => {
    try { const raw = sessionStorage.getItem(`${KEY}.${slug}`); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [status, setStatus] = useState<string>('');
  const [media, setMedia] = useState<MediaKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const mediaRoom = useRef<Room | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [connected, setConnected] = useState(false);
  const mediaKind = useRef<'watch' | 'full' | null>(null);
  const seatsRef = useRef<Seat[]>([]);
  useEffect(() => { seatsRef.current = seats; }, [seats]);
  // G10: the comment on the stage right now, as the host or a moderator put it there.
  const [banner, setBanner] = useState<Extract<StageMessage, { type: 'comment' }> | null>(null);
  const [comments, setComments] = useState<RoomComment[]>([]);
  const [poll, setPoll] = useState<RoomPoll | null>(null);
  // G17: reactions — mine go up at once; the room's arrive with the next count and float over the picture.
  const [floaters, setFloaters] = useState<string[]>([]);
  const reactSeen = useRef<Record<string, number>>({});
  const reactBusy = useRef(false);
  const [others, setOthers] = useState<{ id: string; name: string; status: RoomGuest['status']; role: string | null; question: string | null }[]>([]);
  const [draft, setDraft] = useState('');
  const [tip, setTip] = useState(0);
  const [posting, setPosting] = useState<string | null>(null);
  const moderator = status === 'moderator';

  useEffect(() => {
    api.greenRoom(slug).then(setRoom).catch(() => setError('This door is closed.'));
  }, [slug]);
  // The door is a page of its own: its ground reaches the edges of the window, not just the column.
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = room?.theme === 'light' ? '#fafaf7' : '#101418';
    return () => { document.body.style.background = prev; };
  }, [room?.theme]);

  useEffect(() => {
    if (!guest) return;
    let live = true;
    // One pulse carries the state, the comments, the poll, the room's feeling, and the door for a moderator.
    const tick = () => api.greenPulse(slug, guest.id, guest.secret)
      .then((s) => { if (!live) return; setStatus(s.status); setRoom((cur) => ({ ...(cur ?? {}), ...s.room }));
                     setComments(s.comments); setPoll(s.poll); if (s.moderator) setOthers(s.guests);
                     const b = reactionBurst(reactSeen.current, s.reactions); reactSeen.current = b.seen; if (b.float.length) setFloaters(b.float);
                     // a fresh key every tick would reconnect every tick: keep the one in hand until its kind changes
                     setMedia((cur) => (cur && !!cur.watch === !!s.media?.watch ? cur : s.media));
                     if (s.profile && !savedProfile) { setProfile(s.profile); setSavedProfile(JSON.stringify(s.profile)); } })
      .catch(() => { if (live) setError('This door is closed.'); });
    void tick();
    // A door left open in a background tab pulsed the API every 2.5 s for twelve
    // hours on 2026-10-13 (17,600 requests) and kept its instance awake the whole
    // time. Hidden, the door asks every 30 s — enough to notice being let in —
    // and asks at once, then every 2.5 s again, the moment it is looked at.
    const every = () => (document.hidden ? 30_000 : 2500);
    let t = setInterval(tick, every());
    const onVisibility = () => { clearInterval(t); if (!document.hidden) void tick(); t = setInterval(tick, every()); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { live = false; clearInterval(t); document.removeEventListener('visibilitychange', onVisibility); };
  }, [guest, slug]);

  // The media key arrives once admitted: connect, hand the camera to the
  // room (the local preview stops so the device is not held twice), and show
  // whoever else is in it — the host, the other guests.
  // G13: at the door the key is watch-only — they see and hear the show while
  // they get ready, unseen. Once admitted the key changes and they join for real.
  useEffect(() => {
    if (!media) return;
    const kind = media.watch ? 'watch' : 'full';
    if (mediaRoom.current && mediaKind.current === kind) return;
    if (mediaRoom.current) { void mediaRoom.current.disconnect(); mediaRoom.current = null; setConnected(false); setSeats([]); }
    let cancelled = false;
    if (kind === 'full') stream.current?.getTracks().forEach((t) => t.stop());
    const onData = (bytes: Uint8Array, from: string | null) => {
      const fromHost = !!from && from.startsWith('host-');
      const fromMod = !!from && seatsRef.current.some((s) => s.identity === from && s.meta.kind === 'moderator');
      if (!fromHost && !fromMod) return;
      const m = decodeStageMessage(bytes);
      if (m?.type === 'comment') setBanner(m); else if (m?.type === 'clear') setBanner(null);
    };
    joinMedia(media, setSeats, { publish: kind === 'full', onData })
      .then((room) => {
        if (cancelled) { void room.disconnect(); return; }
        mediaRoom.current = room; mediaKind.current = kind; setConnected(true);
        if (kind === 'full') {
          const cam = room.localParticipant.getTrackPublication('camera' as never)?.track;
          if (cam && video.current) cam.attach(video.current);
        }
      })
      .catch((e) => setError(`could not join the room's media: ${(e as Error).message}`));
    return () => { cancelled = true; };
  }, [media]);
  useEffect(() => () => { void mediaRoom.current?.disconnect(); }, []);

  // G7: the guest's own choices — which camera and microphone, and who they
  // are on screen. Devices are listed once the first stream has opened (the
  // browser hides labels before permission).
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [camId, setCamId] = useState('');
  const [micId, setMicId] = useState('');
  const [profile, setProfile] = useState<GuestProfile>({});
  const [savedProfile, setSavedProfile] = useState<string>('');
  const [savingProfile, setSavingProfile] = useState(false);

  async function openCamera(cam = camId, mic = micId) {
    try {
      stream.current?.getTracks().forEach((t) => t.stop());
      const s = await navigator.mediaDevices.getUserMedia({
        video: cam ? { deviceId: { exact: cam }, width: { ideal: 1280 } } : { width: { ideal: 1280 } },
        audio: mic ? { deviceId: { exact: mic } } : true,
      });
      stream.current = s;
      if (video.current) { video.current.srcObject = s; await video.current.play().catch(() => undefined); }
      try { setDevices(await navigator.mediaDevices.enumerateDevices()); } catch { /* no labels: the defaults stay */ }
    } catch { setError('The camera did not open. You can still wait to be brought up.'); }
  }

  /** A small square JPEG from the camera, for when the camera is off. Under 48 KB. */
  function takePicture(): string | null {
    const v = video.current;
    if (!v || !v.videoWidth) return null;
    const side = Math.min(v.videoWidth, v.videoHeight);
    const c = document.createElement('canvas'); c.width = 240; c.height = 240;
    const ctx = c.getContext('2d'); if (!ctx) return null;
    ctx.translate(240, 0); ctx.scale(-1, 1);   // as the mirror shows it
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, 240, 240);
    let q = 0.8; let url = c.toDataURL('image/jpeg', q);
    while (url.length > 48_000 && q > 0.3) { q -= 0.1; url = c.toDataURL('image/jpeg', q); }
    return url.length <= 65_536 ? url : null;
  }

  async function saveProfile(next: GuestProfile) {
    if (!guest) return;
    setProfile(next);
    const key = JSON.stringify(next);
    if (key === savedProfile) return;
    setSavingProfile(true);
    try {
      await api.greenProfile(slug, guest.id, guest.secret, {
        role: next.role ?? '', location: next.location ?? '', handle: next.handle ?? '', pronouns: next.pronouns ?? '',
        hue: next.hue ?? null, avatar: next.avatar ?? '', question: next.question ?? '', cameraOff: Boolean(next.camera_off),
      });
      setSavedProfile(key);
    } catch (e) { setError((e as Error).message); }
    finally { setSavingProfile(false); }
  }

  async function join() {
    if (!name.trim()) return;
    try {
      const g = await api.greenJoin(slug, name.trim());
      const next = { id: g.guestId, secret: g.secret };
      try { sessionStorage.setItem(`${KEY}.${slug}`, JSON.stringify(next)); } catch { /* per-viewer convenience */ }
      setGuest(next);
      void openCamera();
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => { if (!guest) { setComments([]); setOthers([]); setPoll(null); } }, [guest]);

  async function react(emoji: string) {
    if (!guest || reactBusy.current) return;
    reactBusy.current = true; setFloaters([emoji]);
    try { await api.greenReact(slug, guest.id, guest.secret, emoji); } catch { /* one a second; a tap too soon is nothing */ }
    setTimeout(() => { reactBusy.current = false; }, 1000);
  }

  async function vote(choice: number) {
    if (!guest || !poll) return;
    try { await api.greenVote(slug, guest.id, guest.secret, poll.id, choice); setPoll(await api.greenPoll(slug, guest.id, guest.secret)); }
    catch (e) { setError((e as Error).message); }
  }

  async function postComment() {
    if (!guest || !draft.trim()) return;
    setPosting('sending');
    try {
      await api.greenComment(slug, guest.id, guest.secret, draft.trim(), tip || undefined);
      setDraft(''); setTip(0); setPosting(null);
      const c = await api.greenComments(slug, guest.id, guest.secret); setComments(c);
    } catch (e) { setPosting((e as Error).message); }
  }

  async function moderate(action: { target: string; move: 'admit' | 'hold' | 'remove' | 'on-air' } | { comment: string; status: 'shown' | 'hidden' }) {
    if (!guest) return;
    try {
      await api.greenModerate(slug, guest.id, guest.secret, action);
      if ('comment' in action && mediaRoom.current) {
        const c = comments.find((x) => x.id === action.comment);
        const msg: StageMessage = action.status === 'shown' && c
          ? { type: 'comment', id: c.id, name: c.name, text: c.text, amountCents: c.amount_cents || undefined, pos: room?.bannerPos ?? 'bottom', accent: room?.accent ?? null, xy: room?.bannerXY ?? null }
          : { type: 'clear' };
        await sendToRoom(mediaRoom.current, encodeStageMessage(msg)).catch(() => undefined);
      }
      const [c, g] = await Promise.all([api.greenComments(slug, guest.id, guest.secret), api.greenGuests(slug, guest.id, guest.secret)]);
      setComments(c); setOthers(g);
    } catch (e) { setError((e as Error).message); }
  }

  async function leave() {
    if (guest) { await api.greenLeave(slug, guest.id, guest.secret).catch(() => undefined); }
    try { sessionStorage.removeItem(`${KEY}.${slug}`); } catch { /* ignore */ }
    stream.current?.getTracks().forEach((t) => t.stop());
    await mediaRoom.current?.disconnect(); mediaRoom.current = null; setConnected(false); setSeats([]);
    setGuest(null); setStatus(''); setMedia(null);
  }

  const line = status === 'waiting' ? (media?.watch ? 'You are at the door. Watch the show below while you get ready; the host will bring you in.' : 'You are at the door. The host will bring you in.')
    : status === 'moderator' ? 'You are a moderator: you keep the door and decide which comments the audience sees.'
    : status === 'held' ? 'The host has you on hold — stay close.'
    : status === 'admitted' ? 'You are in the room. The host will bring you up when it is your turn.'
    : status === 'on_air' ? 'You are ON AIR.'
    : status === 'removed' ? 'The host has ended your visit.'
    : status === 'left' ? 'You left the room.'
    : '';

  return (
    <main className="door" data-theme={room?.theme ?? 'dark'} style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px', fontFamily: 'system-ui, sans-serif', minHeight: '100vh', boxSizing: 'border-box',
                  ...(room?.theme === 'light'
                    ? { '--door-bg': '#fafaf7', '--door-ink': '#16181c', '--door-ink-2': '#3a3d44', '--door-muted': '#6b7078', '--door-line': '#dcdfe4', '--door-panel': '#ffffff', '--door-field': '#ffffff' }
                    : { '--door-bg': '#101418', '--door-ink': '#f2f4f7', '--door-ink-2': '#c7ccd4', '--door-muted': '#8b939e', '--door-line': '#2a3038', '--door-panel': '#171c22', '--door-field': '#1c2229' }) as React.CSSProperties,
                  background: 'var(--door-bg)', color: 'var(--door-ink)' }}>
      <style>{`.door input, .door textarea, .door select { background: var(--door-field); color: var(--door-ink); border: 1px solid var(--door-line); border-radius: 6px; }
                .door details, .door section { background: var(--door-panel); } .door a { color: inherit; }`}</style>
      {/* G8: the door is a front page — the show's picture, its name, what it is about. */}
      <header className="door-hd" style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 16, borderLeft: `6px solid ${room?.accent ?? room?.brandAccent ?? '#e0b100'}`, paddingLeft: 14 }}>
        {room?.picture && <img src={room.picture} alt="" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 8, flex: 'none', background: '#111' }} />}
        <div style={{ minWidth: 0 }}>
          {(room?.logo || room?.brandName) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }} data-tool="door-brand">
              {room.logo && <img src={room.logo} alt="" style={{ height: 28, maxWidth: 140, objectFit: 'contain' }} />}
              {room.brandName && <span style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--door-muted)' }}>{room.brandName}</span>}
            </div>
          )}
          {room?.welcome && <p style={{ margin: '0 0 2px', fontSize: 15, color: 'var(--door-ink-2)' }} data-tool="door-welcome">{room.welcome}</p>}
          <h1 style={{ fontSize: 24, margin: 0 }}>{room?.name ?? 'Green room'}</h1>
          {room?.description && <p style={{ margin: '4px 0 0', color: 'var(--door-ink-2)' }}>{room.description}</p>}
          <p style={{ color: 'var(--door-muted)', margin: '4px 0 0', fontSize: 13 }}>
            {room?.status === 'live' ? '● The show is live.' : nextLiveLine(room?.nextAt) ?? 'The show is getting ready.'}
          </p>
        </div>
      </header>
      {room?.links && room.links.length > 0 && (
        <nav data-tool="door-links" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {room.links.map((l) => <a key={l.url} className="btn tiny" href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a>)}
        </nav>
      )}
      {room?.rules && (
        <details open={!guest} style={{ marginBottom: 12, border: '1px solid var(--door-line)', borderRadius: 8, padding: '8px 12px' }} data-tool="house-rules">
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>House rules</summary>
          <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0', color: 'var(--door-ink-2)' }}>{room.rules}</p>
          <p style={{ color: 'var(--door-muted)', fontSize: 12, margin: '6px 0 0' }}>Moderators keep the room to these. A guest who does not is shown the door.</p>
        </details>
      )}
      {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
      {!guest ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input value={name} maxLength={60} placeholder="Your name, as it should appear" onChange={(e) => setName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') void join(); }} style={{ flex: 1, minWidth: 220, padding: 8 }} />
          <button className="btn pri" data-tool="knock" onClick={() => void join()} disabled={!name.trim() || !!error} style={{ padding: '8px 18px', fontSize: 15 }}>Knock</button>
        </div>
      ) : (
        <div>
          <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden', aspectRatio: '16 / 9', maxWidth: '100%' }}>
            <video ref={video} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
            {status === 'on_air' && <span style={{ position: 'absolute', top: 12, left: 12, background: '#dc2626', color: 'white', padding: '4px 10px', borderRadius: 6, fontWeight: 700 }}>● ON AIR</span>}
            <Floaters queue={floaters} />
          </div>
          <div data-tool="react-bar" style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8 }}>
            {EMOJI.map((e) => <button key={e} onClick={() => void react(e)} title="Let the room know" style={{ fontSize: 22, lineHeight: 1, padding: '6px 10px', borderRadius: 999, border: '1px solid var(--door-line)', background: 'var(--door-panel)', cursor: 'pointer' }}>{e}</button>)}
          </div>
          <p style={{ fontSize: 18, marginBlock: 12 }}>{line}</p>
          <details open style={{ marginBottom: 12, border: '1px solid var(--door-line)', borderRadius: 8, padding: '8px 12px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>You, on screen</summary>
            <p style={{ color: 'var(--door-muted)', marginTop: 6 }}>What the host sees and what goes under your name when you are on air. Everything here is yours to change.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
              <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>What you do
                <input maxLength={60} value={profile.role ?? ''} placeholder="Grower · Editor · Fan since 2019" onChange={(e) => setProfile({ ...profile, role: e.target.value })} onBlur={() => void saveProfile(profile)} style={{ padding: 6 }} /></label>
              <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>Where you are
                <input maxLength={60} value={profile.location ?? ''} placeholder="Las Vegas" onChange={(e) => setProfile({ ...profile, location: e.target.value })} onBlur={() => void saveProfile(profile)} style={{ padding: 6 }} /></label>
              <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>Your handle
                <input maxLength={41} value={profile.handle ?? ''} placeholder="@you" onChange={(e) => setProfile({ ...profile, handle: e.target.value.replace(/[^@A-Za-z0-9_.]/g, '') })} onBlur={() => void saveProfile(profile)} style={{ padding: 6 }} /></label>
              <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>Pronouns (optional)
                <input maxLength={20} value={profile.pronouns ?? ''} placeholder="she/her" onChange={(e) => setProfile({ ...profile, pronouns: e.target.value })} onBlur={() => void saveProfile(profile)} style={{ padding: 6 }} /></label>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <span style={{ fontSize: 13 }}>Your colour</span>
              {[0, 30, 60, 120, 180, 210, 260, 300, 330].map((h) => (
                <button key={h} type="button" aria-label={`hue ${h}`} onClick={() => void saveProfile({ ...profile, hue: h })}
                        style={{ width: 24, height: 24, borderRadius: '50%', border: profile.hue === h ? '3px solid #111' : '1px solid #999', background: `hsl(${h} 55% 45%)` }} />
              ))}
              <span style={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `hsl(${profile.hue ?? 210} 55% 45%)`, color: 'white', fontWeight: 700 }}
                    title="Shown when your camera is off">{(room ? name : '').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?'}</span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <button type="button" className="btn tiny" onClick={() => { const a = takePicture(); if (a) void saveProfile({ ...profile, avatar: a }); else setError('The camera has no picture yet.'); }} style={{ padding: '6px 10px' }}>Take my picture</button>
              {profile.avatar && <img src={profile.avatar} alt="your picture" width={48} height={48} style={{ borderRadius: '50%', objectFit: 'cover' }} />}
              {profile.avatar && <button type="button" className="btn tiny" onClick={() => void saveProfile({ ...profile, avatar: '' })} style={{ padding: '6px 10px' }}>Remove</button>}
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={Boolean(profile.camera_off)} onChange={(e) => void saveProfile({ ...profile, camera_off: e.target.checked })} /> Show my picture instead of my camera</label>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <label style={{ fontSize: 13 }}>Camera{' '}
                <select value={camId} onChange={(e) => { setCamId(e.target.value); void openCamera(e.target.value, micId); }}>
                  <option value="">default</option>
                  {devices.filter((d) => d.kind === 'videoinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'camera'}</option>)}
                </select></label>
              <label style={{ fontSize: 13 }}>Microphone{' '}
                <select value={micId} onChange={(e) => { setMicId(e.target.value); void openCamera(camId, e.target.value); }}>
                  <option value="">default</option>
                  {devices.filter((d) => d.kind === 'audioinput').map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'microphone'}</option>)}
                </select></label>
            </div>
            <label style={{ display: 'grid', gap: 2, fontSize: 13, marginTop: 8 }}>A question for the host (optional)
              <textarea rows={2} maxLength={300} value={profile.question ?? ''} placeholder="What you came to ask" onChange={(e) => setProfile({ ...profile, question: e.target.value })} onBlur={() => void saveProfile(profile)} style={{ padding: 6, resize: 'vertical' }} /></label>
            <p style={{ color: 'var(--door-muted)', fontSize: 12, margin: '6px 0 0' }}>{savingProfile ? 'Saving…' : 'Saved when you leave a field.'}</p>
          </details>
          {(seats.length > 0 || media?.watch) && (
            <section data-tool="watch-the-show" style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, margin: '0 0 6px', color: 'var(--door-ink-2)' }}>{media?.watch ? 'The show, as it goes out' : 'In the room with you'}</h3>
              {banner && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '8px 12px', borderRadius: 8, background: '#111', color: 'white', marginBottom: 8, borderLeft: `6px solid ${banner.accent ?? room?.accent ?? '#e0b100'}` }}>
                  <strong>{banner.name}</strong>{banner.amountCents ? <span style={{ background: banner.accent ?? '#e0b100', color: '#111', borderRadius: 999, padding: '0 8px', fontWeight: 700 }}>{money(banner.amountCents)}</span> : null}
                  <span>{banner.text}</span>
                </div>
              )}
              {seats.length === 0 && <p style={{ color: 'var(--door-muted)', fontSize: 13 }}>{connected ? 'Nobody is on yet. You will see the host and the guests here as they come in.' : 'Connecting…'}</p>}
              {seats.find((s) => s.screen) && (
                <div data-tool="door-screen" style={{ background: '#000', borderRadius: 6, aspectRatio: '16 / 9', overflow: 'hidden', marginBottom: 8 }}
                     ref={(box) => { const el = seats.find((s) => s.screen)?.screen ?? null; if (box && el && el.parentElement !== box) { box.replaceChildren(el); el.style.width = '100%'; el.style.height = '100%'; el.style.objectFit = 'contain'; } }} />
              )}
              <div style={{ display: 'grid', gridTemplateColumns: seats.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {seats.map((s) => (
                  <div key={s.identity} style={{ background: '#111', borderRadius: 6, aspectRatio: '16 / 9', overflow: 'hidden', position: 'relative' }}>
                    <div ref={(box) => mount(box, s.element)} style={{ width: '100%', height: '100%' }} />
                    <span style={{ position: 'absolute', left: 6, bottom: 4, color: 'white', fontSize: 12, textShadow: '0 1px 3px #000' }}>{s.name}{s.meta.role ? ` · ${s.meta.role}` : ''}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* G16: the poll — answer once, change your mind while it is open, watch the bars move. */}
          {poll && (
            <section data-tool="door-poll" style={{ border: '1px solid var(--door-line)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <h3 style={{ fontSize: 14, margin: 0, flex: 1 }}>{poll.question}</h3>
                <span style={{ fontSize: 11, color: 'var(--door-muted)' }}>{poll.status === 'open' ? (poll.mine == null ? 'tap an answer' : 'you answered — tap another to change') : 'closed'} · {poll.tallies.reduce((a, b) => a + b, 0)} votes</span>
              </div>
              <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                {poll.options.map((o, i) => { const pct = pollShares(poll.tallies)[i] ?? 0; const mine = poll.mine === i; return (
                  <button key={i} disabled={poll.status !== 'open'} onClick={() => void vote(i)}
                          style={{ position: 'relative', textAlign: 'left', padding: '8px 10px', borderRadius: 6, border: `1px solid ${mine ? (room?.accent ?? '#e0b100') : 'var(--door-line)'}`, background: 'var(--door-field)', color: 'var(--door-ink)', overflow: 'hidden', cursor: poll.status === 'open' ? 'pointer' : 'default' }}>
                    <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: room?.accent ?? '#e0b100', opacity: .25, transition: 'width .4s' }} />
                    <span style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}><span>{mine ? '● ' : ''}{o}</span><strong>{pct}%</strong></span>
                  </button>); })}
              </div>
            </section>
          )}
          {/* G10–G12: the conversation. Everyone comments; the host and moderators decide what is shown, here and on the stage. */}
          <section data-tool="live-chat" style={{ border: '1px solid var(--door-line)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <h3 style={{ fontSize: 14, margin: 0 }}>Comments</h3>
              <span style={{ flex: 1 }} />
              {room?.donateUrl && (
                <a className="btn tiny pri" href={room.donateUrl} target="_blank" rel="noopener noreferrer" data-tool="tip-the-show">{room.donateLabel || 'Tip the show'}</a>
              )}
            </div>
            <div style={{ maxHeight: 260, overflowY: 'auto', marginBlock: 8 }}>
              {comments.length === 0 && <p style={{ color: 'var(--door-muted)', fontSize: 13, margin: 0 }}>Nothing yet. Say hello.</p>}
              {comments.map((c) => (
                <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '4px 0', borderTop: '1px solid var(--door-line)', opacity: c.status === 'hidden' ? .5 : 1 }}>
                  <strong style={{ fontSize: 13 }}>{c.name}</strong>
                  {c.amount_cents > 0 && <span style={{ background: isSuperChat(c, room?.superchatMin ?? 0) ? (room?.accent ?? '#e0b100') : 'var(--door-line)', color: 'var(--door-ink)', borderRadius: 999, padding: '0 8px', fontSize: 12, fontWeight: 700 }} title="What they told us they tipped">{money(c.amount_cents)}</span>}
                  <span style={{ fontSize: 14, flex: 1 }}>{c.text}</span>
                  {c.status === 'pending' && <span style={{ fontSize: 11, color: 'var(--door-muted)' }}>{c.mine ? 'waiting for the host' : 'waiting'}</span>}
                  {c.status === 'hidden' && <span style={{ fontSize: 11, color: 'var(--door-muted)' }}>hidden</span>}
                  {moderator && c.status !== 'shown' && <button className="btn tiny" onClick={() => void moderate({ comment: c.id, status: 'shown' })}>Show</button>}
                  {moderator && c.status !== 'hidden' && <button className="btn tiny" onClick={() => void moderate({ comment: c.id, status: 'hidden' })}>Hide</button>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <input value={draft} maxLength={300} placeholder="Say something to the show" onChange={(e) => setDraft(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter') void postComment(); }} style={{ flex: 1, minWidth: 200, padding: 6 }} />
              {room?.donateUrl && (
                <select value={tip} onChange={(e) => setTip(Number(e.target.value))} title="Tip the show first, then say how much: a super chat goes on the screen" style={{ padding: 6 }}>
                  <option value={0}>no tip</option>
                  {[200, 500, 1000, 2000, 5000].map((n) => <option key={n} value={n}>tipped {money(n)}</option>)}
                </select>
              )}
              <button className="btn" disabled={!draft.trim() || posting === 'sending'} onClick={() => void postComment()}>Send</button>
            </div>
            {posting && posting !== 'sending' && <p role="alert" style={{ color: '#b91c1c', fontSize: 12, margin: '4px 0 0' }}>{posting}</p>}
            <p style={{ color: 'var(--door-muted)', fontSize: 12, margin: '6px 0 0' }}>
              The host or a moderator shows a comment to everyone — here and on the broadcast.
              {room?.donateUrl ? ` Tips go straight to the show through its own link; one of ${money(room.superchatMin ?? 0)} or more is a super chat and can go on the screen.` : ''}
            </p>
          </section>

          {moderator && (
            <section data-tool="moderator-desk" style={{ border: '1px solid var(--door-line)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>The door ({others.length})</h3>
              {others.filter((g) => g.id !== guest.id).map((g) => (
                <div key={g.id} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--door-line)', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: 'white', background: g.status === 'on_air' ? '#dc2626' : g.status === 'waiting' ? '#2563eb' : '#555', borderRadius: 999, padding: '0 8px' }}>{g.status.replace('_', ' ')}</span>
                  <strong style={{ fontSize: 13 }}>{g.name}</strong>{g.role && <span style={{ fontSize: 12, color: 'var(--door-muted)' }}>{g.role}</span>}
                  {g.question && <span style={{ fontSize: 12, color: 'var(--door-muted)' }}>❝ {g.question}</span>}
                  <span style={{ flex: 1 }} />
                  {(g.status === 'waiting' || g.status === 'held') && <button className="btn tiny" onClick={() => void moderate({ target: g.id, move: 'admit' })}>Admit</button>}
                  {g.status === 'admitted' && <button className="btn tiny" onClick={() => void moderate({ target: g.id, move: 'on-air' })}>Bring up</button>}
                  {g.status === 'on_air' && <button className="btn tiny" onClick={() => void moderate({ target: g.id, move: 'admit' })}>Send back</button>}
                  {g.status !== 'held' && g.status !== 'moderator' && <button className="btn tiny" onClick={() => void moderate({ target: g.id, move: 'hold' })}>Hold</button>}
                  {g.status !== 'moderator' && <button className="btn tiny" onClick={() => void moderate({ target: g.id, move: 'remove' })}>Remove</button>}
                </div>
              ))}
              {others.filter((g) => g.id !== guest.id).length === 0 && <p style={{ color: 'var(--door-muted)', fontSize: 13, margin: 0 }}>Nobody else at the door.</p>}
            </section>
          )}
          {media && !media.watch ? <p style={{ color: 'var(--door-muted)' }}>{connected ? 'You are connected — the host can see and hear you.' : 'Connecting to the room…'}</p>
                 : (status === 'admitted' || status === 'on_air') && <p style={{ color: 'var(--door-muted)' }}>The host will bring your sound and picture in on their end.</p>}
          <button className="btn" onClick={() => void leave()} style={{ padding: '8px 14px' }}>Leave</button>
        </div>
      )}
    </main>
  );
}
